import { pairingRequest } from './connection';

// A small DataChannel-compatible transport for networks that block peer traffic.
export function createHttpChannel({ room, token, role }) {
    const host = role === 'host';
    const abort = new AbortController();
    const timers = new Set();
    const failures = { read: 0, write: 0 };
    let lastSnapshot;
    let pending = null;
    let writing = false;
    let writeTimer = false;
    let lastWrite = -Infinity;
    const channel = {
        readyState: 'connecting', onopen: null, onmessage: null, onclose: null, onerror: null,
        send(data) {
            if (channel.readyState === 'closed') return;
            const message = JSON.parse(data);
            if (host && message.type === 'playback') {
                // A later heartbeat must not swallow a queued explicit seek.
                pending = { ...message, seek: !!(message.seek || (pending?.seek && pending.video === message.video)) };
            } else if (!host && ['status', 'ready'].includes(message.type)) {
                pending = { state: message.type === 'ready' ? 'ready' : message.state, transport: 'http' };
            } else return;
            scheduleWrite();
        },
        close() { finish(false); },
    };
    const schedule = (fn, delay) => {
        if (channel.readyState === 'closed') return;
        const timer = setTimeout(() => { timers.delete(timer); fn(); }, delay);
        timers.add(timer);
    };
    const finish = notify => {
        if (channel.readyState === 'closed') return;
        channel.readyState = 'closed';
        abort.abort();
        timers.forEach(clearTimeout);
        timers.clear();
        if (notify) channel.onclose?.();
    };
    const request = async (path, options) => {
        const operation = options?.method ? 'write' : 'read';
        try {
            const result = await pairingRequest(`/${room.id}/${path}`, { ...options, token, signal: abort.signal });
            if (channel.readyState === 'closed') return undefined;
            failures[operation] = 0;
            return result;
        } catch (error) {
            if (channel.readyState === 'closed') return undefined;
            failures[operation] += 1;
            channel.onerror?.(error);
            if ([403, 404].includes(error.status) || failures[operation] >= 3) finish(true);
            return undefined;
        }
    };
    const open = () => {
        if (channel.readyState !== 'connecting') return;
        channel.readyState = 'open';
        channel.onopen?.();
    };
    const emit = message => channel.onmessage?.({ data: JSON.stringify(message) });
    const poll = async () => {
        const data = await request(host ? 'status' : 'state');
        if (channel.readyState === 'closed') return;
        if (data) {
            const connected = host ? data.connected : data.controllerConnected !== false;
            if (connected) {
                open();
                if (channel.readyState === 'closed') return;
                const snapshot = host ? data.state : (data.revision ?? JSON.stringify(data));
                if (snapshot !== lastSnapshot) {
                    lastSnapshot = snapshot;
                    if (host) emit({ type: 'status', state: data.state });
                    else emit(data);
                }
            } else if (channel.readyState === 'open') {
                finish(true);
                return;
            }
        }
        schedule(poll, host ? 500 : 250);
    };
    async function write() {
        writeTimer = false;
        if (channel.readyState === 'closed' || writing || !pending) return;
        writing = true;
        const snapshot = pending;
        pending = null;
        lastWrite = Date.now();
        const result = await request(host ? 'state' : 'status', { method: 'PUT', body: JSON.stringify(snapshot) });
        writing = false;
        if (result === undefined && channel.readyState !== 'closed') {
            if (!pending) pending = snapshot;
            else if (host) pending = { ...pending, seek: !!(pending.seek || (snapshot.seek && pending.video === snapshot.video)) };
        }
        scheduleWrite();
    }
    function scheduleWrite() {
        if (channel.readyState === 'closed' || writing || writeTimer || !pending) return;
        writeTimer = true;
        schedule(write, Math.max(0, 250 - (Date.now() - lastWrite)));
    }
    // Let callers attach handlers before the first response can arrive.
    Promise.resolve().then(() => {
        if (channel.readyState === 'closed') return;
        poll();
        scheduleWrite();
    });
    return channel;
}
